import {useCallback, useRef, useState} from "react";
import {http as httpApiGateway} from "../../../services/api/restApiClient";
import {pollFileGenerationStatus} from "../../../services/pollFileGenerationStatus";

// On-demand generation is kicked off asynchronously (returns a jobId immediately)
// so it isn't subject to API Gateway's 29s integration timeout — the actual PDF
// generation can take longer and is polled for via pollFileGenerationStatus.

export const useGeneratePdf = ({
  pdfDocumentName,
  workflowId,
  hasScrollEnforcementFeatureFlag,
}: {
  pdfDocumentName: string;
  workflowId: string | null;
  hasScrollEnforcementFeatureFlag: boolean;
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  // Callers auto-start generation whenever there is no file and nothing is generating, which is
  // also the state a failed attempt leaves behind. hasFailed lets them stop instead of retrying
  // a whole server-side generation in a loop.
  const [hasFailed, setHasFailed] = useState(false);
  // A ref rather than isGenerating, so a call made before the re-render is still rejected and
  // generatePdf keeps a stable identity across attempts.
  const isGeneratingRef = useRef(false);

  const generatePdf = useCallback(async () => {
    if (
      !hasScrollEnforcementFeatureFlag ||
      isGeneratingRef.current ||
      !workflowId
    )
      return;

    isGeneratingRef.current = true;
    setIsGenerating(true);
    setHasFailed(false);
    try {
      // Step 1: Kick off generation and get a jobId to poll.
      const generateResponse = await httpApiGateway.get(
        `/pdf/workflows/${workflowId}/generate`,
        {type: "full"},
      );

      const jobId = (generateResponse.data as {jobId?: string})?.jobId;

      if (!jobId) {
        throw new Error("Job id not found in response");
      }

      // Step 2: Poll until the PDF is ready and we get a presigned download URL.
      const downloadUrl = await pollFileGenerationStatus(jobId);

      // Step 3: Fetch the actual PDF bytes from S3 via the presigned URL
      const pdfResponse = await fetch(downloadUrl);

      if (!pdfResponse.ok) {
        throw new Error("Failed to fetch PDF from S3");
      }

      const blob = await pdfResponse.blob();
      const file = new File([blob], pdfDocumentName, {type: "application/pdf"});

      setFile(file);
    } catch (error) {
      setFile(null);
      setHasFailed(true);
    } finally {
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  }, [workflowId, pdfDocumentName, hasScrollEnforcementFeatureFlag]);

  return {
    isGenerating,
    file,
    hasFailed,
    generatePdf,
  };
};

export const downloadPdf = (file: File, pdfDocumentName: string) => {
  if (!file) return;

  const url = window.URL.createObjectURL(file as File);
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.download = pdfDocumentName;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  window.URL.revokeObjectURL(url);
};
